import { computed, makeObservable } from 'mobx';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { EventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRow6Pack/EventRow6Pack.state';
import { getTeamShirt } from './MLBTeamLogos';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { selectBestOverUnderMarketCandidate } from 'src/domains/sportsbook/webview/components/sportEventsListNew/selectBestOverUnderMarket';
import {
    pickPreferredLineMarket,
    preferOpenMarketModels,
} from 'src/domains/sportsbook/webview/components/sportEventsListNew/preferOpenMarkets';

/** Classic / production 6-pack headers */
export const getMarketNameMLB = (
    getTranslation: (key: string, defaultText: string) => string
): [string, string, string] => {
    return [
        getTranslation('market.mlb.moneyline', 'Money Line'),
        getTranslation('market.mlb.runspread', 'Run Spread'),
        getTranslation('market.mlb.totalruns', 'Total Runs'),
    ];
};

/** Genius unified 6-pack headers (design) */
export const getMarketNameMLBUnified = (
    getTranslation: (key: string, defaultText: string) => string
): [string, string, string] => {
    return [
        getTranslation('market.mlb.runline', 'Run Line'),
        getTranslation('market.mlb.total', 'Total'),
        getTranslation('market.mlb.moneyline', 'Money Line'),
    ];
};

const isMoneyLineMarket = (market: MarketModel): boolean => {
    return market.templateId === 'money-line' || market.templateId === 'match-winner';
};

const isRunLineMarket = (market: MarketModel): boolean => {
    return (
        market.templateId === 'run-line' ||
        market.marketName.startsWith('Match handicap') ||
        market.marketName.startsWith('Run Spread') ||
        market.marketName.startsWith('Run Line')
    );
};

/** Production classic filter — intentionally broad (`startsWith('Total Runs')`). */
const isClassicTotalRunsMarket = (market: MarketModel): boolean => {
    return market.marketName.startsWith('Total Runs U/O') || market.marketName.startsWith('Total Runs');
};

const isGeniusTotalRunsMarket = (market: MarketModel): boolean => {
    return (
        market.templateId === 'total-runs-over-under' ||
        market.marketName.startsWith('Total Runs U/O') ||
        market.marketName === 'Total Runs Over/Under'
    );
};

const getSelectionDecimalPrice = (market: MarketModel, selectionTemplateId: 'O' | 'U'): number | null => {
    const selection = market.selections.find((item) => item.templateId === selectionTemplateId);
    const decimalPrice = selection?.price?.d;

    if (decimalPrice === undefined || Number.isFinite(decimalPrice) === false || decimalPrice <= 0) {
        return null;
    }

    return decimalPrice;
};

const findRunLineMarket = (event: EventModel): MarketModel | undefined => {
    const spreads = event.markets.filter(isRunLineMarket);
    return spreads.length === 1 ? spreads[0] : spreads[1] ?? spreads[0];
};

const findRunLineMarketUnified = (event: EventModel): MarketModel | undefined => {
    return pickPreferredLineMarket(event.markets.filter(isRunLineMarket));
};

/** Production: pick second matching Total Runs market when multiple exist. */
const findTotalRunsMarketClassic = (event: EventModel): MarketModel | undefined => {
    const totalRuns = event.markets.filter(isClassicTotalRunsMarket);
    return totalRuns.length === 1 ? totalRuns[0] : totalRuns[1];
};

/** Genius: pick fairest O/U line among open markets (closest to 50/50). */
const findTotalRunsMarketUnified = (event: EventModel): MarketModel | undefined => {
    const totalRuns = preferOpenMarketModels(event.markets.filter(isGeniusTotalRunsMarket));

    if (totalRuns.length === 0) {
        return undefined;
    }

    if (totalRuns.length === 1) {
        return totalRuns[0];
    }

    const candidates = totalRuns.map((market, index) => ({
        id: market.id,
        overPrice: getSelectionDecimalPrice(market, 'O'),
        underPrice: getSelectionDecimalPrice(market, 'U'),
        websiteMain: market.websiteMain,
        displayOrder: market.displayOrder,
        index,
    }));

    const bestCandidate = selectBestOverUnderMarketCandidate(candidates);

    if (bestCandidate === null) {
        return totalRuns[1] ?? totalRuns[0];
    }

    return totalRuns.find((market) => market.id === bestCandidate.id) ?? totalRuns[0];
};

const findTotalRunsMarket = (event: EventModel, isUnifiedEvents: boolean): MarketModel | undefined => {
    return isUnifiedEvents === true ? findTotalRunsMarketUnified(event) : findTotalRunsMarketClassic(event);
};

const findMoneyLineMarket = (event: EventModel): MarketModel | undefined => {
    return event.markets.find(isMoneyLineMarket);
};

const findMoneyLineMarketUnified = (event: EventModel): MarketModel | undefined => {
    return preferOpenMarketModels(event.markets.filter(isMoneyLineMarket))[0];
};

export class MLBEventRow6PackState implements EventRow6PackState {
    public constructor(
        public readonly eventId: EventId,
        public readonly language: LanguagesState,
        public readonly showParticipantLogos: boolean = true,
        public readonly isUnifiedEvents: boolean = false
    ) {
        makeObservable(this);
    }

    @computed public get event(): EventModel | null {
        return this.eventId.getEventModel();
    }

    private get marketNames(): [string, string, string] {
        return this.isUnifiedEvents === true
            ? getMarketNameMLBUnified(this.language.getTranslation)
            : getMarketNameMLB(this.language.getTranslation);
    }

    public get firstMarketName(): string {
        return this.marketNames[0] ?? '-';
    }

    public get secondMarketName(): string {
        return this.marketNames[1] ?? '-';
    }

    public get thirdMarketName(): string {
        return this.marketNames[2] ?? '-';
    }

    @computed public get firstMarket(): MarketModel | undefined {
        const event = this.event;
        if (event === null) {
            return undefined;
        }

        return this.isUnifiedEvents === true ? findRunLineMarketUnified(event) : findMoneyLineMarket(event);
    }

    @computed public get secondMarket(): MarketModel | undefined {
        const event = this.event;
        if (event === null) {
            return undefined;
        }

        return this.isUnifiedEvents === true ? findTotalRunsMarket(event, true) : findRunLineMarket(event);
    }

    @computed public get thirdMarket(): MarketModel | undefined {
        const event = this.event;
        if (event === null) {
            return undefined;
        }

        return this.isUnifiedEvents === true ? findMoneyLineMarketUnified(event) : findTotalRunsMarket(event, false);
    }

    public getSelectionName = (market: MarketModel | undefined, selection: SelectionModel): string => {
        if (market === undefined) {
            return '';
        }

        // Genius design: Money Line shows odds only
        if (this.isUnifiedEvents === true && isMoneyLineMarket(market)) {
            return '';
        }

        if (isClassicTotalRunsMarket(market) || isGeniusTotalRunsMarket(market)) {
            if (
                this.isUnifiedEvents === true &&
                market.line !== null &&
                selection.name.includes(String(market.line)) === false
            ) {
                return `${selection.name} ${market.line}`;
            }
            return selection.name;
        }

        // Classic Money Line / Run Line: last token (team name or handicap)
        return selection.name.split(' ').slice(-1).join(' ');
    };

    public getParticipantLogo = (name: string | null): string | null => {
        if (this.showParticipantLogos === false) {
            return null;
        }

        return getTeamShirt(name);
    };
}
